import { useEffect, useRef } from 'react';
import { Header, ListBox, ListBoxItem, ListBoxSection, Popover, ProgressBar, Text } from '@launchpad-ui/components';
import { type IconName, Icon } from '@launchpad-ui/icons';

import { dedupeOptionsByValue } from './utils/dedupeOptions';

import styles from './AutocompletePopover.module.css';

type Option = {
  value: string | number | boolean;
  displayName?: string;
  resultsCount?: number;
  description?: string;
  category?: string;
  iconName?: IconName;
  disabled?: boolean;
  /** Inline-rendered type chip (e.g. "string", "number") shown after the option label. */
  typeLabel?: string;
  /** Icon rendered at the right edge of the option (e.g. a green check next to a regex match). */
  trailingIconName?: IconName;
  /** Marker class applied to the trailing icon — `success` paints green, `danger` paints red. */
  trailingIconTone?: 'success' | 'danger';
  /**
   * When provided, replaces the rendered label content (the text inside the option's
   * label slot) with this React node. Use it for cases that need formatting inside
   * the label — e.g. wrapping a fragment in <code>. `displayName` is still used for
   * the accessible textValue and the highlight-match search text.
   */
  labelNode?: React.ReactNode;
};

interface AutocompletePopoverProps {
  triggerRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSelect: (value: string | number | boolean) => void;
  options: Option[];
  groupByCategory?: boolean;
  isLoading?: boolean;
  multiSelect?: boolean;
  selectedKeys?: string[];
  /** Index of the virtually-focused option driven by keyboard navigation. -1 or absent = none. */
  focusedIndex?: number;
  /** When provided, the matching substring in each option label is bolded. */
  searchText?: string;
  /** Applied to the Popover element for aria-controls linkage from the combobox trigger. */
  id?: string;
  /** Footer pinned below the options list, outside the scrolling area. Omitted = no footer. */
  footer?: React.ReactNode;
}

function optionId(option: Option): string {
  return String(option.value);
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query) {
    return <>{text}</>;
  }
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) {
    return <>{text}</>;
  }
  return (
    <>
      {text.slice(0, idx)}
      <strong className={styles.highlightedMatch}>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </>
  );
}

function OptionItem({ option, focused, searchText }: { option: Option; focused?: boolean; searchText?: string }) {
  const label = option.displayName ?? String(option.value);
  return (
    <ListBoxItem
      id={optionId(option)}
      className={focused ? `${styles.item} ${styles.itemFocused}` : styles.item}
      // Marks the virtually-focused option so the popover can scroll it into view.
      {...(focused ? { 'data-focused-option': '' } : {})}
      textValue={label}
      isDisabled={option.disabled}
    >
      <Text slot="label" className={styles.label}>
        {option.iconName && <Icon name={option.iconName} size="small" />}
        <span>{option.labelNode ?? (searchText ? <HighlightMatch text={label} query={searchText} /> : label)}</span>
      </Text>
      {option.description && <Text slot="description">{option.description}</Text>}
      {option.typeLabel && <span className={styles.typeLabel}>{option.typeLabel}</span>}
      {option.resultsCount !== undefined && (
        <Text slot="description" className={styles.resultsCount}>
          {option.resultsCount.toLocaleString()}
        </Text>
      )}
      {option.trailingIconName && (
        <span
          className={
            option.trailingIconTone === 'success'
              ? `${styles.trailingIcon} ${styles.trailingIconSuccess}`
              : option.trailingIconTone === 'danger'
                ? `${styles.trailingIcon} ${styles.trailingIconDanger}`
                : styles.trailingIcon
          }
          aria-hidden="true"
        >
          <Icon name={option.trailingIconName} size="small" />
        </span>
      )}
    </ListBoxItem>
  );
}

function FlatList({
  options,
  onSelect,
  onOpenChange,
  multiSelect,
  selectedKeys,
  focusedIndex,
  searchText,
}: Pick<
  AutocompletePopoverProps,
  'options' | 'onSelect' | 'onOpenChange' | 'multiSelect' | 'selectedKeys' | 'focusedIndex' | 'searchText'
>) {
  if (multiSelect) {
    return (
      <ListBox
        aria-label="Options"
        className={styles.listBox}
        selectionMode="multiple"
        selectedKeys={selectedKeys}
        onSelectionChange={(keys) => {
          if (keys === 'all') {
            return;
          }
          const currentSet = new Set(selectedKeys ?? []);
          const newSet = new Set([...keys].map(String));
          const toggled = [...newSet].find((k) => !currentSet.has(k)) ?? [...currentSet].find((k) => !newSet.has(k));
          if (toggled) {
            const match = options.find((o) => optionId(o) === toggled);
            if (match) {
              onSelect(match.value);
            }
          }
        }}
      >
        {options.map((option, index) => (
          <OptionItem key={optionId(option)} option={option} focused={focusedIndex === index} searchText={searchText} />
        ))}
      </ListBox>
    );
  }

  return (
    <ListBox
      aria-label="Options"
      className={styles.listBox}
      selectionMode="single"
      onSelectionChange={(keys) => {
        if (keys === 'all') {
          return;
        }
        const [key] = [...keys];
        if (key === null) {
          return;
        }
        const match = options.find((o) => optionId(o) === String(key));
        if (match) {
          onSelect(match.value);
          onOpenChange(false);
        }
      }}
    >
      {options.map((option, index) => (
        <OptionItem key={optionId(option)} option={option} focused={focusedIndex === index} searchText={searchText} />
      ))}
    </ListBox>
  );
}

function GroupedList({
  options,
  onSelect,
  onOpenChange,
  focusedIndex,
  searchText,
}: Pick<AutocompletePopoverProps, 'options' | 'onSelect' | 'onOpenChange' | 'focusedIndex' | 'searchText'>) {
  const grouped = options.reduce<Record<string, Option[]>>((acc, option) => {
    const key = option.category ?? '';
    return { ...acc, [key]: [...(acc[key] ?? []), option] };
  }, {});

  // Map each option's stable id to its absolute index in the parent options array.
  // useAutocompleteNavigation tracks the flat index across all options, so the
  // grouped render needs to look up each option's true position rather than
  // assuming categories are contiguous (they may be interleaved).
  const flatIndexById = new Map(options.map((o, i) => [optionId(o), i]));

  return (
    <ListBox
      aria-label="Options"
      className={styles.listBox}
      selectionMode="single"
      onSelectionChange={(keys) => {
        if (keys === 'all') {
          return;
        }
        const [key] = [...keys];
        if (key === null) {
          return;
        }
        const match = options.find((o) => optionId(o) === String(key));
        if (match) {
          onSelect(match.value);
          onOpenChange(false);
        }
      }}
    >
      {Object.entries(grouped).map(([category, categoryOptions]) => (
        <ListBoxSection key={category}>
          {category && <Header>{category}</Header>}
          {categoryOptions.map((option) => (
            <OptionItem
              key={optionId(option)}
              option={option}
              focused={flatIndexById.get(optionId(option)) === focusedIndex}
              searchText={searchText}
            />
          ))}
        </ListBoxSection>
      ))}
    </ListBox>
  );
}

export function AutocompletePopover({
  triggerRef,
  isOpen,
  onOpenChange,
  onSelect,
  options: rawOptions,
  groupByCategory = false,
  isLoading = false,
  multiSelect,
  selectedKeys,
  focusedIndex = -1,
  searchText,
  id,
  footer,
}: AutocompletePopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Defensive net for direct callers: callers that drive keyboard navigation must
  // dedupe *before* wiring `focusedIndex` / `onSelect` (see dedupeOptionsByValue),
  // so this is normally a no-op on an already-deduped list.
  const options = dedupeOptionsByValue(rawOptions);

  // Keyboard navigation is virtual (focus stays on the input), so the ListBox
  // never auto-scrolls. When the highlighted option changes, pull it into view
  // so ArrowUp/Down past the visible window scrolls the popover with the highlight.
  // Deferred a frame because the popover renders in a portal that mounts after this
  // effect; scope the lookup to the `id` container (falling back to the popover ref).
  useEffect(() => {
    if (focusedIndex < 0) {
      return undefined;
    }
    const raf = requestAnimationFrame(() => {
      const root: ParentNode | null = (id ? document.getElementById(id) : null) ?? ref.current;
      root?.querySelector<HTMLElement>('[data-focused-option]')?.scrollIntoView?.({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(raf);
  }, [focusedIndex, id]);

  // Nudge RA to re-measure when an ancestor resizes (e.g. nav collapse slides the trigger sideways); RA otherwise only tracks resize/scroll/element-size, not position moves.
  useEffect(() => {
    if (!isOpen || typeof ResizeObserver === 'undefined') {
      return;
    }
    const trig = triggerRef.current;
    if (!trig) {
      return;
    }
    let initialized = false;
    const observer = new ResizeObserver(() => {
      // Skip ResizeObserver's initial observe() fire; only react to real post-open layout changes.
      if (!initialized) {
        initialized = true;
        return;
      }
      window.dispatchEvent(new Event('resize'));
    });
    for (let el: HTMLElement | null = trig; el; el = el.parentElement) {
      observer.observe(el);
    }
    return () => observer.disconnect();
  }, [isOpen, triggerRef]);

  return (
    <Popover
      triggerRef={triggerRef}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement="bottom start"
      // Always render below the filter bar; never flip up.
      shouldFlip={false}
      offset={16}
      className={styles.popover}
      aria-label="Filter options"
      isNonModal
      ref={ref}
    >
      <div id={id}>
        {isLoading ? (
          <div className={styles.loading}>
            <ProgressBar aria-label="Loading options…" isIndeterminate />
          </div>
        ) : options.length === 0 ? (
          <div className={styles.empty}>No options</div>
        ) : groupByCategory ? (
          <GroupedList
            options={options}
            onSelect={onSelect}
            onOpenChange={onOpenChange}
            focusedIndex={focusedIndex}
            searchText={searchText}
          />
        ) : (
          <FlatList
            options={options}
            onSelect={onSelect}
            onOpenChange={onOpenChange}
            multiSelect={multiSelect}
            selectedKeys={selectedKeys}
            focusedIndex={focusedIndex}
            searchText={searchText}
          />
        )}
      </div>
      {footer && (
        // Keep focus on the input (preventDefault) so the popover isn't torn down before the
        // click lands, and stop the click reaching the bar/overlay dismiss handlers.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- propagation guard only, not an interactive control
        <div className={styles.footer} onMouseDown={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()}>
          {footer}
        </div>
      )}
    </Popover>
  );
}

AutocompletePopover.displayName = 'AutocompletePopover';
